import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

// ─── Local Types ──────────────────────────────────────────────

type ComplianceFramework = 'hipaa' | 'soc2' | 'nist' | 'pci_dss' | 'iso27001';

interface BundleCourse {
  title: string;
  type: string;
  duration_minutes?: number;
  passing_score?: number;
  control_ref: string;
  description: string;
}

interface TrainingBundle {
  id: string;
  framework: ComplianceFramework;
  name: string;
  description: string;
  courses: BundleCourse[];
  frequency: 'annual' | 'quarterly' | 'one_time';
  is_active: boolean;
  auto_assign: boolean;
  last_assigned_at?: string;
  next_due_at?: string;
  created_at: string;
}

interface ComplianceTrainingRecord {
  id: string;
  bundle_id: string;
  framework: ComplianceFramework;
  control_ref: string;
  user_id: string;
  user_email: string;
  campaign_id: string;
  status: 'assigned' | 'in_progress' | 'completed' | 'overdue' | 'expired';
  completed_at?: string;
  score?: number;
  evidence_id?: string;
  due_date?: string;
  assigned_at: string;
}

interface SummaryData {
  frameworks_covered: number;
  active_bundles: number;
  total_completions_30d: number;
  completion_rate: number;
  overdue_count: number;
}

interface BundlesResponse {
  data: TrainingBundle[];
}

interface SummaryResponse {
  data: SummaryData;
}

interface RecordsResponse {
  data: ComplianceTrainingRecord[];
}

// ─── Constants ────────────────────────────────────────────────

const FRAMEWORKS: { key: ComplianceFramework; label: string; color: string }[] = [
  { key: 'hipaa', label: 'HIPAA', color: 'bg-blue-100 text-blue-700' },
  { key: 'soc2', label: 'SOC 2', color: 'bg-purple-100 text-purple-700' },
  { key: 'nist', label: 'NIST', color: 'bg-indigo-100 text-indigo-700' },
  { key: 'pci_dss', label: 'PCI DSS', color: 'bg-orange-100 text-orange-700' },
  { key: 'iso27001', label: 'ISO 27001', color: 'bg-teal-100 text-teal-700' },
];

// ─── Helper Components ────────────────────────────────────────

function FrameworkBadge({ framework, isActive }: { framework: ComplianceFramework; isActive: boolean }) {
  const meta = FRAMEWORKS.find(f => f.key === framework);
  const label = meta?.label ?? framework.toUpperCase();
  const colorClasses = isActive ? (meta?.color ?? 'bg-emerald-100 text-emerald-700') : 'bg-gray-100 text-gray-400';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${colorClasses}`}>
      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: ComplianceTrainingRecord['status'] }) {
  const classes =
    status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
    status === 'overdue' ? 'bg-red-100 text-red-700' :
    status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
    status === 'expired' ? 'bg-gray-100 text-gray-500' :
    'bg-blue-100 text-blue-600';
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${classes}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function FrequencyLabel({ frequency }: { frequency: TrainingBundle['frequency'] }) {
  const map: Record<TrainingBundle['frequency'], string> = {
    annual: 'Annual',
    quarterly: 'Quarterly',
    one_time: 'One-time',
  };
  return <span>{map[frequency]}</span>;
}

// ─── Main Page ────────────────────────────────────────────────

export function ComplianceTrainingPage() {
  const { data: bundlesData, loading: bundlesLoading, error: bundlesError, refresh: refreshBundles } =
    useApi<BundlesResponse>('/protect/compliance-training');
  const { data: summaryData, loading: summaryLoading, refresh: refreshSummary } =
    useApi<SummaryResponse>('/protect/compliance-training?action=summary');
  const { data: recordsData, refresh: refreshRecords } =
    useApi<RecordsResponse>('/protect/compliance-training?action=records');

  const [selectedRecordFramework, setSelectedRecordFramework] = useState<string>('');
  const [selectedRecordStatus, setSelectedRecordStatus] = useState<string>('');
  const [viewingBundleId, setViewingBundleId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [markingEvidenceId, setMarkingEvidenceId] = useState<string | null>(null);
  const [evidenceInput, setEvidenceInput] = useState('');
  const [evidenceNotes, setEvidenceNotes] = useState('');

  const bundles = bundlesData?.data ?? [];
  const summary = summaryData?.data;
  const allRecords = recordsData?.data ?? [];

  const activeBundleFrameworks = new Set(bundles.filter(b => b.is_active).map(b => b.framework));

  // Filter records for the records table
  const filteredRecords = allRecords.filter(r => {
    if (viewingBundleId && r.bundle_id !== viewingBundleId) return false;
    if (selectedRecordFramework && r.framework !== selectedRecordFramework) return false;
    if (selectedRecordStatus && r.status !== selectedRecordStatus) return false;
    return true;
  });

  function refreshAll() {
    refreshBundles();
    refreshSummary();
    refreshRecords();
  }

  async function handleActivateBundle(framework: ComplianceFramework) {
    setActionLoading(`activate-${framework}`);
    try {
      await post('/protect/compliance-training', { framework, auto_assign: false });
      refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to activate bundle');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAssignNow(bundleId: string) {
    if (!confirm('Assign this training to all users now?')) return;
    setActionLoading(`assign-${bundleId}`);
    try {
      await post(`/protect/compliance-training/${bundleId}?action=assign_now`);
      refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign training');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleToggleAuto(bundleId: string) {
    setActionLoading(`auto-${bundleId}`);
    try {
      await post(`/protect/compliance-training/${bundleId}?action=toggle_auto`);
      refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to toggle auto-assign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleMarkEvidence(recordId: string) {
    if (!evidenceInput.trim()) {
      toast.error('Evidence ID is required');
      return;
    }
    setActionLoading(`evidence-${recordId}`);
    try {
      await post(`/protect/compliance-training/records/${recordId}?action=mark_evidence`, {
        evidence_id: evidenceInput.trim(),
        notes: evidenceNotes.trim() || undefined,
      });
      setMarkingEvidenceId(null);
      setEvidenceInput('');
      setEvidenceNotes('');
      refreshRecords();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark evidence');
    } finally {
      setActionLoading(null);
    }
  }

  if (bundlesLoading || summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading compliance training...</div>
      </div>
    );
  }

  if (bundlesError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{bundlesError}</p>
        <button onClick={refreshAll} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  // Frameworks without an active bundle (for empty state cards)
  const inactiveFrameworks = FRAMEWORKS.filter(f => !activeBundleFrameworks.has(f.key));

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Compliance Training Library</h2>
      <p className="text-gray-500 text-sm mb-6">
        Auto-assign security training based on compliance framework requirements. Link completions to compliance evidence.
      </p>

      {/* Framework Coverage Header */}
      <div className="flex flex-wrap gap-2 mb-6">
        {FRAMEWORKS.map(f => (
          <FrameworkBadge key={f.key} framework={f.key} isActive={activeBundleFrameworks.has(f.key)} />
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active Bundles</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{summary?.active_bundles ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Users Enrolled (30d)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.total_completions_30d ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completion Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.completion_rate ?? 0}%</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Overdue Records</p>
          <p className={`text-2xl font-bold mt-1 ${(summary?.overdue_count ?? 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {summary?.overdue_count ?? 0}
          </p>
        </div>
      </div>

      {/* Bundles Grid — active bundles */}
      {bundles.length > 0 && (
        <>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Active Bundles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {bundles.map(bundle => {
              const meta = FRAMEWORKS.find(f => f.key === bundle.framework);
              const isAssigning = actionLoading === `assign-${bundle.id}`;
              const isTogglingAuto = actionLoading === `auto-${bundle.id}`;
              return (
                <div key={bundle.id} className="bg-white rounded-lg border border-gray-200 p-5 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${meta?.color ?? 'bg-gray-100 text-gray-600'} mb-1`}>
                        {meta?.label ?? bundle.framework}
                      </span>
                      <h4 className="text-sm font-semibold text-gray-900 leading-snug">{bundle.name}</h4>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${bundle.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {bundle.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed">{bundle.description}</p>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>{bundle.courses.length} courses</span>
                    <span><FrequencyLabel frequency={bundle.frequency} /></span>
                    {bundle.next_due_at && (
                      <span>Next due: {new Date(bundle.next_due_at).toLocaleDateString()}</span>
                    )}
                  </div>

                  {/* Auto-assign toggle */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleAuto(bundle.id)}
                      disabled={isTogglingAuto}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                        bundle.auto_assign ? 'bg-emerald-500' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={bundle.auto_assign}
                    >
                      <span
                        className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${
                          bundle.auto_assign ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                    <span className="text-xs text-gray-500">Auto-assign {bundle.auto_assign ? 'on' : 'off'}</span>
                  </div>

                  <div className="flex gap-2 mt-auto pt-1">
                    <button
                      onClick={() => handleAssignNow(bundle.id)}
                      disabled={isAssigning}
                      className="flex-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {isAssigning ? 'Assigning...' : 'Assign Now'}
                    </button>
                    <button
                      onClick={() => {
                        setViewingBundleId(viewingBundleId === bundle.id ? null : bundle.id);
                        setSelectedRecordFramework('');
                        setSelectedRecordStatus('');
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-200"
                    >
                      {viewingBundleId === bundle.id ? 'Hide Records' : 'View Records'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Inactive framework cards */}
      {inactiveFrameworks.length > 0 && (
        <>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Available Bundles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
            {inactiveFrameworks.map(f => {
              const isActivating = actionLoading === `activate-${f.key}`;
              return (
                <div key={f.key} className="bg-gray-50 rounded-lg border border-dashed border-gray-200 p-5 flex flex-col gap-3">
                  <div>
                    <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-400 mb-1">
                      {f.label}
                    </span>
                    <h4 className="text-sm font-semibold text-gray-500 leading-snug">Not activated</h4>
                  </div>
                  <p className="text-xs text-gray-400">
                    Activate the pre-built {f.label} training bundle to auto-assign compliance training to your users.
                  </p>
                  <button
                    onClick={() => handleActivateBundle(f.key)}
                    disabled={isActivating}
                    className="mt-auto px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    {isActivating ? 'Activating...' : 'Activate Bundle'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Records Table */}
      {(viewingBundleId !== null || allRecords.length > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-800">
              Training Records
              {viewingBundleId && (
                <button
                  onClick={() => setViewingBundleId(null)}
                  className="ml-2 text-xs text-gray-400 hover:text-gray-600 font-normal"
                >
                  (clear filter)
                </button>
              )}
            </h3>
            <div className="flex gap-2">
              <select
                value={selectedRecordFramework}
                onChange={e => setSelectedRecordFramework(e.target.value)}
                className="border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-600"
              >
                <option value="">All Frameworks</option>
                {FRAMEWORKS.map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
              <select
                value={selectedRecordStatus}
                onChange={e => setSelectedRecordStatus(e.target.value)}
                className="border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-600"
              >
                <option value="">All Statuses</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {filteredRecords.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No training records found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Framework</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Control</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Completed</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Score</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Evidence</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map(record => {
                    const isMarkingThis = markingEvidenceId === record.id;
                    const isSaving = actionLoading === `evidence-${record.id}`;
                    return (
                      <>
                        <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900 text-xs">{record.user_email}</td>
                          <td className="px-4 py-3">
                            <FrameworkBadge framework={record.framework} isActive />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate" title={record.control_ref}>
                            {record.control_ref}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={record.status} />
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {record.completed_at ? new Date(record.completed_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-gray-600">
                            {record.score != null ? `${record.score}%` : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {record.evidence_id ? (
                              <span className="text-emerald-600 font-mono text-xs">{record.evidence_id}</span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!record.evidence_id && record.status === 'completed' && (
                              <button
                                onClick={() => {
                                  setMarkingEvidenceId(isMarkingThis ? null : record.id);
                                  setEvidenceInput('');
                                  setEvidenceNotes('');
                                }}
                                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                              >
                                Mark Evidence
                              </button>
                            )}
                          </td>
                        </tr>
                        {isMarkingThis && (
                          <tr key={`${record.id}-evidence`} className="bg-emerald-50 border-b border-gray-100">
                            <td colSpan={8} className="px-4 py-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <input
                                  type="text"
                                  placeholder="Evidence ID (e.g. EVD-2026-001)"
                                  value={evidenceInput}
                                  onChange={e => setEvidenceInput(e.target.value)}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs w-48"
                                />
                                <input
                                  type="text"
                                  placeholder="Notes (optional)"
                                  value={evidenceNotes}
                                  onChange={e => setEvidenceNotes(e.target.value)}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs flex-1 min-w-[160px]"
                                />
                                <button
                                  onClick={() => handleMarkEvidence(record.id)}
                                  disabled={isSaving}
                                  className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={() => setMarkingEvidenceId(null)}
                                  className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
